import type { CodeSessionAgentEvent, CodeSessionEvent } from '@/lib/code-sessions/types';

import { eventData, streamKeyOf } from './agent-events';

const DELTA_PART_TYPE: ReadonlyMap<string, 'text' | 'reasoning'> = new Map([
  ['session.text.delta', 'text'],
  ['session.reasoning.delta', 'reasoning'],
]);

/** Stream key and text of a keyed, non-empty token delta. */
function keyedDelta(event: CodeSessionAgentEvent): { key: string; delta: string } | undefined {
  const partType = DELTA_PART_TYPE.get(event.type);
  if (partType === undefined) {
    return undefined;
  }
  const data = eventData(event);
  const key = streamKeyOf(partType, data);
  return key !== undefined && typeof data.delta === 'string' && data.delta.length > 0
    ? { key, delta: data.delta }
    : undefined;
}

/**
 * A stream's consecutive keyed deltas fold exactly like one delta carrying
 * their joined text: each appends to the part the first one found or
 * started, and nothing else reads them. Streaming delivers one event per few
 * tokens, so joining them keeps `buildTimeline` proportional to the
 * transcript's parts instead of every token it has ever received.
 *
 * The owner appends each merge's new events in seq order and starts a new
 * coalescer when the log is rewritten, so every call joins only new events.
 */
export class StreamDeltaCoalescer {
  private output: CodeSessionAgentEvent[] = [];
  /** The run of deltas the last output event holds. */
  private run: { key: string; at: number; first: CodeSessionAgentEvent; text: string } | undefined;

  /**
   * Adds the agent events among `events` and returns the whole coalesced log:
   * the previous array when none were added, otherwise a new one.
   */
  append(events: readonly CodeSessionEvent[]): CodeSessionAgentEvent[] {
    let output: CodeSessionAgentEvent[] | undefined;
    for (const event of events) {
      if (event.kind !== 'agent') {
        continue;
      }
      output ??= [...this.output];
      const delta = keyedDelta(event);
      const run = this.run;
      if (delta !== undefined && run !== undefined && run.key === delta.key && event.at >= run.at) {
        run.at = event.at;
        run.text += delta.delta;
        output[output.length - 1] = {
          ...run.first,
          data: { ...eventData(run.first), delta: run.text },
        };
        continue;
      }
      this.run =
        delta === undefined
          ? undefined
          : { key: delta.key, at: event.at, first: event, text: delta.delta };
      output.push(event);
    }
    this.output = output ?? this.output;
    return this.output;
  }
}
